import {CommonModule, KeyValue} from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnInit,
    Self,
} from '@angular/core';
import {
    ControlValueAccessor,
    FormControl,
    FormsModule,
    NgControl,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import {NgbModule} from '@ng-bootstrap/ng-bootstrap';
import {v4 as uuidv4} from 'uuid';
import {ControlMessagesComponent} from '../control-messages/control-messages.component';
import {PopoverIconComponent} from '../popover-icon/popover-icon.component';

@Component({
    selector: 'app-input-select-option-group',
    templateUrl: './input-select-option-group.component.html',
    styleUrls: ['./input-select-option-group.component.scss'],
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        NgbModule,
        ControlMessagesComponent,
        PopoverIconComponent,
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputSelectOptionGroupComponent implements ControlValueAccessor, OnInit {
    @Input() label = '';
    @Input({required: true}) data!: ReadonlyMap<string | number | null, string>;
    @Input() sortOrder = 'asc';
    @Input() information: string | undefined;
    /**
     * When set, renders the Bootstrap grid layout (label in a `col-{labelCols}`, select in the rest)
     * instead of the default stacked layout. ngwebbill passes a value (0 = no label / full-width
     * select) to match its legacy app-local component; other apps leave it unset.
     */
    @Input() labelCols: number | null = null;
    /**
     * Explicit required flag. When provided it wins; when left undefined the asterisk is derived
     * from a `Validators.required` on the control (matching InputTextGroupComponent).
     */
    @Input() required: boolean | undefined;

    formControl = new FormControl('');
    id: string = uuidv4();
    order:
        | ((
              a: KeyValue<string | number | null, string>,
              b: KeyValue<string | number | null, string>,
          ) => number)
        | undefined;
    onTouch: (() => void) | undefined;

    private onChange: ((value: string | null) => void) | undefined;

    constructor(
        @Self() public ngControl: NgControl,
        private changeDetectorRef: ChangeDetectorRef,
    ) {
        ngControl.valueAccessor = this;
    }

    get labelClass(): string {
        const required = this.required === true ? 'required' : '';
        // Grid layout adds the Bootstrap column class; the stacked layout uses just `required`.
        return this.labelCols != null ? `col-${this.labelCols} ${required}`.trim() : required;
    }

    get inputColsClass(): string {
        return this.labelCols != null ? `col-${12 - this.labelCols}` : '';
    }

    ngOnInit(): void {
        // Then using ChangeDetectionStrategy.OnPush the component doesn't notice, when the control is marked as
        // touched, and will therefore not show validation errors. However, Angular doesn't offer a simple way of
        // listening to, when a control is marked as touched, that we can hook into. So we have to reassign the
        // markAsTouched() method in order to call detectChanges().
        if (this.ngControl.control != null) {
            const originalMarkAsTouched = this.ngControl.control.markAsTouched.bind(
                this.ngControl.control,
            );
            const changeDetectorRef = this.changeDetectorRef;
            this.ngControl.control.markAsTouched = function (...args): void {
                originalMarkAsTouched.apply(this, args);
                changeDetectorRef.detectChanges();
            };
        }

        this.formControl.valueChanges.subscribe(data => {
            if (this.onChange == null) {
                return;
            }
            this.onChange(data);
            if (!this.formControl.valid) {
                this.ngControl.control?.setErrors(this.formControl.errors);
            }
        });
        // Explicit [required] wins; otherwise the asterisk is derived from the control's
        // Validators.required (matching InputTextGroupComponent).
        this.required =
            this.required ?? this.ngControl.control?.hasValidator(Validators.required) ?? false;

        if (this.sortOrder === 'asc') {
            this.order = this.orderAsc;
        } else if (this.sortOrder === 'dsc') {
            this.order = this.orderDsc;
        } else if (this.sortOrder === 'keys') {
            this.order = this.orderKeys;
        } else {
            this.order = this.orderOriginal;
        }
    }

    registerOnChange(fn: (value: string | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouch = fn;
    }

    writeValue(value: string): void {
        this.formControl.setValue(value);
    }

    setDisabledState(isDisabled: boolean): void {
        if (isDisabled) {
            this.formControl.disable();
        } else {
            this.formControl.enable();
        }
    }

    orderAsc = (
        a: KeyValue<string | number | null, string>,
        b: KeyValue<string | number | null, string>,
    ): number =>
        a.value.toUpperCase() > b.value.toUpperCase()
            ? 1
            : a.value.toUpperCase() < b.value.toUpperCase()
              ? -1
              : 0;

    orderDsc = (
        a: KeyValue<string | number | null, string>,
        b: KeyValue<string | number | null, string>,
    ): number => this.orderAsc(b, a);

    orderKeys = (
        a: KeyValue<string | number | null, string>,
        b: KeyValue<string | number | null, string>,
    ): number => Number(a.key) - Number(b.key);

    orderOriginal = (): number => 0;
}
